import {
  GRC_PLATFORM,
  ReadOnlyFieldDetail,
} from '@trustero/trustero-api-web/lib/common/grc_platform_pb'
import { useImportedRecordModal } from 'src/components/ModalForms/useImportedRecordModal'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { ControlServicePromiseClient } from '@trustero/trustero-api-web/lib/model/control_grpc_web_pb'
import {
  CONTROL_READ_ONLY_FIELDS,
  ControlReadOnlyField,
  ControlReadOnlyFields,
  UpdateControlReadOnlyFieldsRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'

export const useDismissControlReadOnlyWarning = (
  controlId: string,
  fields: CONTROL_READ_ONLY_FIELDS[],
): (() => Promise<void>) => {
  const controlClient = useAuthorizedGrpcClient(ControlServicePromiseClient)

  return async () => {
    const request = new UpdateControlReadOnlyFieldsRequest()
      .setControlId(controlId)
      .setUpdates(
        new ControlReadOnlyFields().setFieldsList([
          ...fields.map((field) =>
            new ControlReadOnlyField()
              .setField(field)
              .setDetails(
                new ReadOnlyFieldDetail().setIsWarningDismissed(
                  new BoolValue().setValue(true),
                ),
              ),
          ),
        ]),
      )
    await controlClient.updateControlReadOnlyFields(request)
  }
}

export const useImportedControlModalOnClick = ({
  grcPlatform,
  showModal,
  controlId,
  fields,
  preserveUpdatedStateOnConfirm,
}: {
  grcPlatform?: GRC_PLATFORM
  showModal?: boolean
  controlId: string
  fields: CONTROL_READ_ONLY_FIELDS[]
  preserveUpdatedStateOnConfirm?: boolean
}): ((onClick: () => void) => Promise<void>) => {
  const dismissWarningFunction = useDismissControlReadOnlyWarning(
    controlId,
    fields,
  )

  const importedRecordModal = useImportedRecordModal({
    modelType: MODEL_TYPE.CONTROL,
    grcPlatform,
    onUpdate: async (onClick: () => void) => {
      await onClick()
    },
    showModal,
    dismissWarningFunction,
    preserveUpdatedStateOnConfirm,
  })

  return async (onClick: () => void) => {
    await importedRecordModal(onClick)
  }
}

export const useUpdateControlWithConfirmationModal = <T extends unknown[]>({
  callback,
  controlId,
  fields,
  grcPlatform,
  showModal,
}: {
  callback: (...args: T) => Promise<void>
  controlId: string
  fields: CONTROL_READ_ONLY_FIELDS[]
  grcPlatform?: GRC_PLATFORM
  showModal?: boolean
}): ((...args: T) => Promise<void>) => {
  const dismissWarningFunction = useDismissControlReadOnlyWarning(
    controlId,
    fields,
  )

  const importedRecordModal = useImportedRecordModal({
    modelType: MODEL_TYPE.CONTROL,
    grcPlatform,
    onUpdate: callback,
    showModal,
    dismissWarningFunction,
  })

  return async (...args: T) => {
    await importedRecordModal(...args)
  }
}
